import React, { useRef, useEffect, useMemo } from "react";
import { PageData, RowVal, PageDataData } from "./useFinancialData";
import { BalanceAggregateType } from "./DashboardFinancial";
import { gettext, View } from "../../shared/utils/legacy";
import cropr from "../../shared/utils/cropr";
import useCachedUrlData from "../../shared/hooks/useCachedUrlData";
import { aggregates, dividePageDataPerHa } from "./utils";
import "./DashboardFinancialTableYears.scss";

const [startCol, endCol] = [4, 7];

interface DashboardFinancialTableYearsProps {
  page: PageData;
  aggregate?: BalanceAggregateType;
  nrOfYears?: number;
  perHa?: boolean;
}
interface SummaryBase {
  groups: { name: string; header: Array<{ index: number; headerName: string | null }> }[];
  data: Map<string, RowVal>[];
}

const getBaseSummary = (pages: PageDataData[]): SummaryBase => {
  // const pges = pages.slice(0, pages.length - 1);
  const names = Object.keys(
    pages.reduce<any>((memo, { rows }) => {
      for (let i = 0; i < rows.length - 1; i++) {
        const name = rows[i][0];
        if (!memo[name]) {
          memo[name] = true;
        }
      }
      return memo;
    }, {})
  ).sort();

  // name+subheaders per page, for mapping as children in columndefs
  const groups = pages.map(({ name, header }, index) => ({
    name,
    header: header.slice(startCol, endCol).map((headerName, i) => ({
      index: index * 3 + i,
      headerName,
    })),
  }));

  // name --> float[]
  const calc = Object.fromEntries(
    names.map((n) => [n, ({ name: n } as unknown) as Map<string, RowVal>])
  );
  pages.forEach(({ rows }, index) => {
    for (let i = 0; i < rows.length - 1; i++) {
      const row = rows[i];
      const calcArray = calc[row[0]];
      row.slice(startCol, endCol).forEach((value, j) => {
        // @ts-ignore
        calcArray[`c_${index * 3 + j}`] = value;
      });
    }
  });
  return {
    groups,
    data: names.map((name) => calc[name]),
  };
};

const DashboardFinancialTableYears = ({
  page,
  aggregate,
  nrOfYears = 3,
  perHa = false,
}: DashboardFinancialTableYearsProps) => {
  const ref = useRef(null);
  const aggrid = useRef(null);
  const activePlan = cropr.getActivePlan();
  const plans = useMemo(() => {
    return cropr.plans.reduce<number[]>((memo, plan) => {
      if (
        plan.id !== activePlan.id &&
        memo.length < nrOfYears &&
        plan.get("period_end") <= activePlan.get("period_end")
      )
        memo.push(plan.id);
      return memo;
    }, []);
  }, [activePlan, nrOfYears]);
  const url = plans.length
    ? `/cropr/export/summary_balance_plans/${plans.join(",")}/${aggregates[aggregate || 0]}`
    : "";
  const history: PageDataData[] | undefined = useCachedUrlData<PageDataData[]>(url).data;
  useEffect(() => {
    if (!(ref?.current && history)) return;
    const rest = perHa ? history.map(dividePageDataPerHa) : history;
    // @ts-ignore
    const { groups, data } = getBaseSummary(
      [{ ...page.data[0].data, name: activePlan.get("name") }].concat(rest)
    );
    const props = {
      el: ref.current,
      table_id: "table_years",
      hideSearch: true,
      groupHeaderHeight: 25,
      defaultColDef: {
        checkboxSelection: false,
        sortable: true,
        resizable: true,
        suppressMovable: true,
      },
      columnDefs: [{ headerName: gettext("Crop"), field: "name" } as any].concat(
        groups.map(({ name, header }, groupId) => ({
          enableValue: groupId === 0,
          headerName: name,
          children: header.map(({ index, headerName }, i) => ({
            hide: i > 0,
            headerName,
            field: `c_${index}`,
            filter: "agNumberColumnFilter",
            cellRenderer: "roundedCurrencyRenderer",
            cellClass: "text-right",
            headerClass: "align-right",
          })),
        }))
      ),
      rowData: data,
      enableRangeSelection: true,
      enableCharts: true,
    };
    if (aggrid.current === null) {
      aggrid.current = new View.AgGridView(props);
    } else {
      // weet niet zeker of dit voldoende is, overgang is wel mooi soepel
      // @ts-ignore
      aggrid.current.setRowData(props.rowData);
    }
  }, [ref, history, activePlan, page, perHa]);
  return (
    <div className="dashboard-financial-table-years" ref={ref}>
      Loading
    </div>
  );
};
export default DashboardFinancialTableYears;
